import { ethers } from "ethers";
import {
  createMasterRecoveryWallet,
  masterRecoveryWalletFromPhrase,
  masterRecoveryWalletToPhrase,
  mergeMasterRecoveryBundles,
  type MasterRecoveryBundle,
  type MasterRecoveryWallet,
} from "./masterRecovery";
import { recoveryBuffer } from "./onchainRecoveryCodec";
import type {
  RecoveryPhraseOptions,
  RecoveryPhraseScope,
} from "./recoveryPhrase";

export interface MasterRecoverySession {
  wallet: MasterRecoveryWallet;
  bundle: MasterRecoveryBundle;
}

export interface MasterRecoveryStorageOptions extends RecoveryPhraseOptions {
  /** Explicit restore/switch only; preserves the prior encrypted record first. */
  allowIdentityReplacement?: boolean;
}

interface StoredMasterRecord {
  version: 1;
  fingerprint: string;
  nonce: string;
  ciphertext: string;
}

const PREFIX = "stealthos_master_recovery_v1:";
const VAULT_DOMAIN = "StealthOS master recovery device vault v1";
const MAX_VAULT_BYTES = 16384;
const EMPTY: MasterRecoveryBundle = { version: 1, revision: 0, entries: [] };
let generation = 0;
let active: MasterRecoverySession | null = null;
let activeWallets = new Set<string>();
let authorized = new WeakMap<MasterRecoverySession, Set<string>>();

export class MasterRecoveryStorageError extends Error {}

function fail(
  message = "Could not unlock this device's master wallet. Restore your StealthOS recovery phrase.",
): never {
  throw new MasterRecoveryStorageError(message);
}

function assertCurrent(expected: number, options: RecoveryPhraseOptions): void {
  if (generation !== expected || (options.isCurrent && !options.isCurrent()))
    fail("Wallet connection changed during recovery. Reconnect and retry.");
}

function targetParts(target: RecoveryPhraseScope): [string, string] {
  if (
    !target ||
    typeof target.scope !== "string" ||
    typeof target.wallet !== "string" ||
    !target.scope ||
    !target.wallet ||
    target.scope.length > 512 ||
    target.wallet.length > 256
  )
    return fail("Invalid master-wallet storage scope.");
  const parts = target.scope.split(":");
  const family = parts[0] === "master-v1" ? parts[1] : parts[0];
  if (family !== "evm" && family !== "solana")
    return fail("Invalid master-wallet storage scope.");
  const wallet = family === "evm" ? target.wallet.toLowerCase() : target.wallet;
  return [target.scope, wallet];
}

function publicWalletIdentity(target: RecoveryPhraseScope): string {
  const [scope, wallet] = targetParts(target);
  const parts = scope.split(":");
  return JSON.stringify([
    parts[0] === "master-v1" ? parts[1] : parts[0],
    wallet,
  ]);
}

function storageKey(target: RecoveryPhraseScope): string {
  return `${PREFIX}${JSON.stringify(targetParts(target))}`;
}

function storage(): Storage {
  try {
    if (typeof localStorage === "undefined") throw new Error();
    return localStorage;
  } catch {
    return fail(
      "Master-wallet storage is unavailable. Keep your recovery phrase and enable device storage.",
    );
  }
}

function read(key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return fail(
      "Master-wallet storage could not be read. Keep your recovery phrase.",
    );
  }
}

function cloneSession(session: MasterRecoverySession): MasterRecoverySession {
  const phrase = masterRecoveryWalletToPhrase(session.wallet);
  return {
    wallet: masterRecoveryWalletFromPhrase(phrase),
    bundle: mergeMasterRecoveryBundles([session.bundle]),
  };
}

function authorize(
  session: MasterRecoverySession,
  wallets: Iterable<string>,
): MasterRecoverySession {
  authorized.set(session, new Set(wallets));
  return session;
}

function activate(
  session: MasterRecoverySession,
  wallets: Set<string>,
): MasterRecoverySession {
  clearMasterRecoverySession();
  active = cloneSession(session);
  activeWallets = new Set(wallets);
  return authorize(cloneSession(active), activeWallets);
}

/** Clear module-owned secrets, e.g. on explicit disconnect or lock. */
export function clearMasterRecoverySession(): void {
  generation += 1;
  if (active) {
    active.wallet.rootKey.fill(0);
    active.wallet.recoveryEntropy.fill(0);
    active.bundle.entries.forEach((entry) => entry.spendingKey.fill(0));
  }
  active = null;
  activeWallets = new Set();
  authorized = new WeakMap();
}

/** Only for an explicit cross-wallet linking choice; never an automatic connection fallback. */
export function getMasterRecoverySessionForLink(): MasterRecoverySession | null {
  return active ? authorize(cloneSession(active), activeWallets) : null;
}

export function hasActiveMasterRecoverySession(): boolean {
  return active !== null;
}

/** Returns nothing for another public wallet until explicit link/restore. */
export function getMasterRecoverySession(
  target: RecoveryPhraseScope,
): MasterRecoverySession | null {
  if (!active || !activeWallets.has(publicWalletIdentity(target))) return null;
  return authorize(cloneSession(active), activeWallets);
}

export function hasStoredMasterRecoverySession(
  target: RecoveryPhraseScope,
): boolean {
  return read(storageKey(target)) !== null;
}

/** Public wrapper headers only. Preserved backups are not active wallets. */
function storedTargets(): RecoveryPhraseScope[] {
  const result: RecoveryPhraseScope[] = [];
  const device = storage();
  for (let index = 0; index < device.length; index++) {
    const key = device.key(index);
    if (!key?.startsWith(PREFIX) || key.includes(":preserved:")) continue;
    try {
      const parts = JSON.parse(key.slice(PREFIX.length));
      if (!Array.isArray(parts) || parts.length !== 2) continue;
      const target = { scope: parts[0], wallet: parts[1] };
      if (storageKey(target) === key) result.push(target);
    } catch {
      /* Unrecognized records are never deleted or modified. */
    }
  }
  return result;
}

export function hasAnyStoredMasterRecoverySession(): boolean {
  return storedTargets().length > 0;
}

/**
 * Never start a different private root for the same public account, including
 * a saved wrapper in another chain scope. The original identity must be
 * unlocked or restored, not silently replaced.
 */
export function hasStoredMasterRecoverySessionForPublicWallet(
  target: RecoveryPhraseScope,
): boolean {
  const identity = publicWalletIdentity(target);
  return storedTargets().some(
    (candidate) => publicWalletIdentity(candidate) === identity,
  );
}

/** A full page navigation clears memory. The verified, chain-independent EVM
 * setup signature can unlock this same public wallet's other scoped wrappers.
 * Never try another account's records or choose between conflicting roots. */
export async function loadSamePublicWalletMasterRecoverySession(
  target: RecoveryPhraseScope,
  signature: Uint8Array,
  options: RecoveryPhraseOptions = {},
): Promise<MasterRecoverySession | null> {
  const expected = generation;
  assertCurrent(expected, options);
  const identity = publicWalletIdentity(target);
  const candidates = storedTargets().filter(
    (candidate) => publicWalletIdentity(candidate) === identity,
  );
  if (!candidates.length) return null;
  const records = candidates.map((candidate) => ({
    target: candidate,
    raw: read(storageKey(candidate)),
  }));
  const sessions: MasterRecoverySession[] = [];
  try {
    for (const record of records) {
      if (record.raw === null)
        return fail(
          "Master-wallet storage changed during recovery. Retry safely.",
        );
      sessions.push(await unseal(record.raw, record.target, signature));
      assertCurrent(expected, options);
    }
    if (
      new Set(sessions.map((session) => fingerprint(session.wallet))).size !== 1
    )
      return fail(
        "This public wallet has different saved StealthOS wallets. Restore the recovery phrase for the wallet you want to use.",
      );
    if (
      records.some((record) => read(storageKey(record.target)) !== record.raw)
    )
      return fail(
        "Master-wallet storage changed during recovery. Retry safely.",
      );
    const session = {
      wallet: sessions[0].wallet,
      bundle: mergeMasterRecoveryBundles(sessions.map((item) => item.bundle)),
    };
    return activate(session, new Set([identity]));
  } finally {
    for (const session of sessions) {
      session.wallet.rootKey.fill(0);
      session.wallet.recoveryEntropy.fill(0);
      session.bundle.entries.forEach((entry) => entry.spendingKey.fill(0));
    }
  }
}

export function masterRecoverySessionPhrase(
  session: MasterRecoverySession,
): string {
  return masterRecoveryWalletToPhrase(session.wallet);
}

function aad(target: RecoveryPhraseScope): Uint8Array {
  return ethers.toUtf8Bytes(
    JSON.stringify([VAULT_DOMAIN, ...targetParts(target)]),
  );
}

function fingerprint(wallet: MasterRecoveryWallet): string {
  return ethers.sha256(
    ethers.concat([
      ethers.toUtf8Bytes("StealthOS master recovery fingerprint v1"),
      wallet.rootKey,
    ]),
  );
}

async function vaultKey(
  signature: Uint8Array,
  target: RecoveryPhraseScope,
  usage: KeyUsage,
): Promise<CryptoKey> {
  if (
    !(signature instanceof Uint8Array) ||
    ![64, 65].includes(signature.length) ||
    signature.every((byte) => byte === 0)
  )
    return fail("Invalid verified wallet setup signature.");
  const material = await crypto.subtle.importKey(
    "raw",
    recoveryBuffer(signature),
    "HKDF",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: recoveryBuffer(ethers.toUtf8Bytes(VAULT_DOMAIN)),
      info: recoveryBuffer(aad(target)),
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    [usage],
  );
}

function encodeSession(session: MasterRecoverySession): Uint8Array {
  const value = cloneSession(session);
  try {
    return ethers.toUtf8Bytes(
      JSON.stringify({
        version: 1,
        entropy: ethers.hexlify(value.wallet.recoveryEntropy),
        bundle: {
          ...value.bundle,
          entries: value.bundle.entries.map((entry) => ({
            ...entry,
            spendingKey: ethers.hexlify(entry.spendingKey),
          })),
        },
      }),
    );
  } finally {
    value.wallet.rootKey.fill(0);
    value.wallet.recoveryEntropy.fill(0);
    value.bundle.entries.forEach((entry) => entry.spendingKey.fill(0));
  }
}

function decodeSession(bytes: Uint8Array): MasterRecoverySession {
  if (bytes.length > MAX_VAULT_BYTES) return fail();
  try {
    const parsed = JSON.parse(ethers.toUtf8String(bytes));
    if (
      parsed.version !== 1 ||
      !ethers.isHexString(parsed.entropy, 16) ||
      !parsed.bundle ||
      !Array.isArray(parsed.bundle.entries)
    )
      return fail();
    const wallet = masterRecoveryWalletFromPhrase(
      ethers.Mnemonic.fromEntropy(parsed.entropy, "", ethers.wordlists.en)
        .phrase,
    );
    const bundle = mergeMasterRecoveryBundles([
      {
        version: parsed.bundle.version,
        revision: parsed.bundle.revision,
        entries: parsed.bundle.entries.map((entry: any) => ({
          network: entry.network,
          primary: entry.primary,
          spendingKey: ethers.isHexString(entry.spendingKey, 32)
            ? ethers.getBytes(entry.spendingKey)
            : new Uint8Array(),
        })),
      },
    ]);
    return { wallet, bundle };
  } catch {
    return fail();
  }
}

async function unseal(
  raw: string,
  target: RecoveryPhraseScope,
  signature: Uint8Array,
): Promise<MasterRecoverySession> {
  try {
    if (raw.length > MAX_VAULT_BYTES * 3) return fail();
    const record = JSON.parse(raw) as StoredMasterRecord;
    if (
      record.version !== 1 ||
      !ethers.isHexString(record.fingerprint, 32) ||
      !ethers.isHexString(record.nonce, 12) ||
      !ethers.isHexString(record.ciphertext) ||
      record.ciphertext.length > MAX_VAULT_BYTES * 2 + 34
    )
      return fail();
    const key = await vaultKey(signature, target, "decrypt");
    const plaintext = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: recoveryBuffer(ethers.getBytes(record.nonce)),
          additionalData: recoveryBuffer(aad(target)),
          tagLength: 128,
        },
        key,
        recoveryBuffer(ethers.getBytes(record.ciphertext)),
      ),
    );
    try {
      const session = decodeSession(plaintext);
      if (fingerprint(session.wallet) !== record.fingerprint) return fail();
      return session;
    } finally {
      plaintext.fill(0);
    }
  } catch {
    return fail();
  }
}

export async function loadMasterRecoverySession(
  target: RecoveryPhraseScope,
  signature: Uint8Array,
  options: RecoveryPhraseOptions = {},
): Promise<MasterRecoverySession | null> {
  const expected = generation;
  assertCurrent(expected, options);
  const key = storageKey(target);
  const raw = read(key);
  if (raw === null) return null;
  const session = await unseal(raw, target, signature);
  assertCurrent(expected, options);
  if (read(key) !== raw)
    return fail(
      "Master-wallet storage changed during recovery. Retry before continuing.",
    );
  const same =
    active && fingerprint(active.wallet) === fingerprint(session.wallet);
  const wallets = new Set(same ? activeWallets : []);
  wallets.add(publicWalletIdentity(target));
  return activate(session, wallets);
}

function preserve(
  key: string,
  raw: string,
  expected: number,
  options: RecoveryPhraseOptions,
): void {
  const backup = `${key}:preserved:${ethers.sha256(ethers.toUtf8Bytes(raw))}`;
  assertCurrent(expected, options);
  const prior = read(backup);
  if (prior !== null && prior !== raw)
    return fail(
      "An encrypted wallet backup conflicts with existing data. Original wallet retained.",
    );
  if (prior === null) {
    try {
      storage().setItem(backup, raw);
    } catch {
      return fail(
        "Could not preserve the prior encrypted wallet. Original wallet retained.",
      );
    }
  }
  if (read(backup) !== raw)
    return fail(
      "Could not verify the prior encrypted wallet backup. Original wallet retained.",
    );
}

export async function persistMasterRecoverySession(
  session: MasterRecoverySession,
  target: RecoveryPhraseScope,
  signature: Uint8Array,
  options: MasterRecoveryStorageOptions = {},
): Promise<MasterRecoverySession> {
  const expected = generation;
  assertCurrent(expected, options);
  const wallets = authorized.get(session);
  if (!wallets?.has(publicWalletIdentity(target)))
    return fail(
      "Explicitly link or restore your master wallet before using another public wallet.",
    );
  const next = cloneSession(session);
  const key = storageKey(target);
  const original = read(key);
  let preserveOriginal = false;
  if (original !== null) {
    try {
      const previous = await unseal(original, target, signature);
      if (fingerprint(previous.wallet) !== fingerprint(next.wallet)) {
        if (!options.allowIdentityReplacement)
          return fail(
            "This public wallet already has a different master wallet. Restore or switch it explicitly.",
          );
        preserveOriginal = true;
      } else {
        next.bundle = mergeMasterRecoveryBundles([
          previous.bundle,
          next.bundle,
        ]);
      }
    } catch (error) {
      if (!options.allowIdentityReplacement) throw error;
      preserveOriginal = true;
    }
  }
  assertCurrent(expected, options);
  const plaintext = encodeSession(next);
  let raw: string;
  try {
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const encryptionKey = await vaultKey(signature, target, "encrypt");
    const ciphertext = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: recoveryBuffer(nonce),
        additionalData: recoveryBuffer(aad(target)),
        tagLength: 128,
      },
      encryptionKey,
      recoveryBuffer(plaintext),
    );
    raw = JSON.stringify({
      version: 1,
      fingerprint: fingerprint(next.wallet),
      nonce: ethers.hexlify(nonce),
      ciphertext: ethers.hexlify(new Uint8Array(ciphertext)),
    } satisfies StoredMasterRecord);
  } finally {
    plaintext.fill(0);
  }
  assertCurrent(expected, options);
  if (read(key) !== original)
    return fail(
      "Master-wallet storage changed during recovery. Original wallet retained.",
    );
  if (original !== null && preserveOriginal)
    preserve(key, original, expected, options);
  assertCurrent(expected, options);
  if (read(key) !== original)
    return fail(
      "Master-wallet storage changed during recovery. Original wallet retained.",
    );
  try {
    storage().setItem(key, raw);
  } catch {
    return fail(
      "Could not save the encrypted master wallet. Keep your recovery phrase.",
    );
  }
  if (read(key) !== raw)
    return fail(
      "Could not verify the saved master wallet. Keep your recovery phrase.",
    );
  return activate(next, wallets);
}

/** Explicit new master creation; callers must first offer import for existing chain wallets. */
export async function createMasterRecoverySession(
  target: RecoveryPhraseScope,
  signature: Uint8Array,
  options: MasterRecoveryStorageOptions = {},
  bundle: MasterRecoveryBundle = EMPTY,
): Promise<MasterRecoverySession> {
  const session = authorize(
    {
      wallet: createMasterRecoveryWallet(),
      bundle: mergeMasterRecoveryBundles([bundle]),
    },
    [publicWalletIdentity(target)],
  );
  return persistMasterRecoverySession(session, target, signature, options);
}

export async function restoreMasterRecoverySession(
  target: RecoveryPhraseScope,
  signature: Uint8Array,
  phrase: string,
  bundle: MasterRecoveryBundle = EMPTY,
  options: MasterRecoveryStorageOptions = {},
): Promise<MasterRecoverySession> {
  const session = authorize(
    {
      wallet: masterRecoveryWalletFromPhrase(phrase),
      bundle: mergeMasterRecoveryBundles([bundle]),
    },
    [publicWalletIdentity(target)],
  );
  return persistMasterRecoverySession(session, target, signature, options);
}

/** Call only after an explicit user choice to link this different public wallet. */
export async function linkMasterRecoverySession(
  session: MasterRecoverySession,
  target: RecoveryPhraseScope,
  signature: Uint8Array,
  options: MasterRecoveryStorageOptions = {},
): Promise<MasterRecoverySession> {
  const previous = authorized.get(session);
  if (!previous)
    return fail(
      "The master wallet is locked. Restore or unlock it before linking another wallet.",
    );
  const wallets = new Set(previous);
  wallets.add(publicWalletIdentity(target));
  return persistMasterRecoverySession(
    authorize(cloneSession(session), wallets),
    target,
    signature,
    options,
  );
}
